import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    AlertTitle,
    Box,
    IconButton,
    InputAdornment,
    CircularProgress,
    Typography,
    Paper,
    Stepper,
    Step,
    StepLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Wifi as WifiIcon,
    Router as RouterIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useToast } from '../toast/useToast';
import { useQuery } from '@tanstack/react-query';
import IotService from '../../api/JsdhNotifikaceService/service/IotService';
import {
    IOT_QUERY_KEYS,
    useMqttCredentialsQuery,
    useShowMqttPasswordMutation,
    useDownloadZigbeeConfigMutation,
    useDownloadBootstrapConfigMutation,
} from '../../api/JsdhNotifikaceService/service/IotMutations';
import { POVOLIT_PRIDANI_ZIGBEE } from '../../api/JsdhNotifikaceService/service/IotZarizeniService';
import type { IotObjektDetail } from '../../api/JsdhNotifikaceService/generated';

type DeviceType = 'SHELLY' | 'RPI';

interface OnboardingWizardProps {
    open: boolean;
    onClose: () => void;
    /** Pokud je znam objekt (spusteni z detailu), zobrazi se konkretni topic prefix a probehne overeni. */
    objektId?: number | null;
    objektNazev?: string;
}

const STEP_LABELS_VSE = ['Typ zařízení', 'MQTT přístup', 'Nastavení v zařízení', 'Ověření připojení'];
/** Bez volby typu (Zigbee/Raspberry Pi zatím skryté) průvodce přeskočí první krok. */
const stepLabels = (): string[] => (POVOLIT_PRIDANI_ZIGBEE ? STEP_LABELS_VSE : STEP_LABELS_VSE.slice(1));

/** Stejna validace ID zarizeni jako v ZarizeniDialog. */
const SHELLY_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

const VERIFY_TIMEOUT_MS = 120_000; // ~2 min

/** Rozlozi broker URL na host a port (Shelly appka je zadava zvlast). */
const parseBroker = (url?: string): { host: string; port: string } => {
    if (!url) return { host: '', port: '' };
    let s = url.replace(/^[a-zA-Z]+:\/\//, ''); // odstranit schema (ssl://, mqtts://, wss://...)
    s = s.split('/')[0]; // odstranit pripadnou cestu
    const idx = s.lastIndexOf(':');
    if (idx === -1) return { host: s, port: '' };
    return { host: s.slice(0, idx), port: s.slice(idx + 1) };
};

const getSafeFileName = (nazev?: string): string =>
    (nazev || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase() || 'objekt';

/** Read-only pole s tlacitkem pro kopirovani. */
const CopyField: React.FC<{
    label: string;
    value: string;
    helperText?: string;
    onCopy: (value: string, label: string) => void;
}> = ({ label, value, helperText, onCopy }) => (
    <TextField
        label={label}
        value={value}
        fullWidth
        margin="normal"
        disabled
        size="small"
        helperText={helperText}
        InputProps={{
            endAdornment: value ? (
                <InputAdornment position="end">
                    <IconButton onClick={() => onCopy(value, label)} size="small" edge="end">
                        <CopyIcon fontSize="small" />
                    </IconButton>
                </InputAdornment>
            ) : undefined,
        }}
    />
);

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
    open,
    onClose,
    objektId,
    objektNazev,
}) => {
    const { showToast } = useToast();

    const [activeStep, setActiveStep] = useState(0);
    // Logický krok (0 = typ zařízení) nezávisle na tom, zda je volba typu zobrazená.
    const krok = POVOLIT_PRIDANI_ZIGBEE ? activeStep : activeStep + 1;
    const [deviceType, setDeviceType] = useState<DeviceType>('SHELLY');
    const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showTimeoutHint, setShowTimeoutHint] = useState(false);
    const [shellyId, setShellyId] = useState('');

    const { data: credentials, isLoading: credentialsLoading } = useMqttCredentialsQuery(open);
    const showPasswordMutation = useShowMqttPasswordMutation();
    const downloadConfigMutation = useDownloadZigbeeConfigMutation();
    const downloadBootstrapMutation = useDownloadBootstrapConfigMutation();

    const hasObjekt = objektId != null && objektId > 0;

    // Reset stavu pri kazdem otevreni
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setDeviceType('SHELLY');
            setRevealedPassword(null);
            setShowPassword(false);
            setShowTimeoutHint(false);
            setShellyId('');
        }
    }, [open]);

    // --- Krok 4: overeni pripojeni (polling seznamu senzoru existujici query) ---
    const verificationActive = open && krok === 3 && hasObjekt;

    const { data: verifObjekt } = useQuery<IotObjektDetail, Error>({
        queryKey: IOT_QUERY_KEYS.objekt(objektId ?? 0),
        queryFn: () => IotService.getObjektById(objektId as number),
        enabled: verificationActive,
        // Polling běží jen během ověřování a zastaví se, jakmile se objeví první senzor.
        refetchInterval: (query) =>
            verificationActive && (query.state.data?.senzory?.length ?? 0) === 0 ? 5000 : false,
    });

    const sensorCount = verifObjekt?.senzory?.length ?? 0;
    const sensorDetected = verificationActive && sensorCount > 0;

    // Nenavtiravy timeout: po ~2 min bez dat zobraz napovedu (nikdy neblokuje zavreni)
    useEffect(() => {
        if (!verificationActive || sensorDetected) {
            setShowTimeoutHint(false);
            return;
        }
        const t = setTimeout(() => setShowTimeoutHint(true), VERIFY_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, [verificationActive, sensorDetected]);

    const handleCopy = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showToast(`${label} zkopírováno do schránky`, 'success');
        } catch {
            showToast('Nepodařilo se zkopírovat do schránky', 'error');
        }
    };

    const handleShowPassword = () => {
        showPasswordMutation.mutate(undefined, {
            onSuccess: (data) => {
                if (data.password) {
                    setRevealedPassword(data.password);
                    setShowPassword(true);
                } else {
                    showToast(
                        'Heslo zatím nebylo vygenerováno — vygenerujte ho v IoT Nastavení → MQTT.',
                        'warning',
                    );
                }
            },
        });
    };

    const handleDownloadBootstrap = () => {
        if (!hasObjekt) return;
        downloadBootstrapMutation.mutate(objektId as number, {
            onSuccess: (configContent) => {
                const blob = new Blob([configContent], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'jsdh-config.json';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                showToast('Bootstrap konfigurace stažena', 'success');
            },
        });
    };

    const handleDownloadConfig = () => {
        if (!hasObjekt) return;
        downloadConfigMutation.mutate(objektId as number, {
            onSuccess: (configContent) => {
                const blob = new Blob([configContent], { type: 'text/yaml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `configuration-${getSafeFileName(objektNazev)}.yaml`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                showToast('Zigbee2MQTT konfigurace stažena', 'success');
            },
        });
    };

    const isLastStep = activeStep === stepLabels().length - 1;

    const handleNext = () => {
        if (isLastStep) {
            onClose();
        } else {
            setActiveStep((s) => s + 1);
        }
    };

    const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

    const broker = parseBroker(credentials?.brokerUrl);
    const username = credentials?.username ?? '';
    const shellyIdTrim = shellyId.trim();
    const shellyIdValid = SHELLY_ID_RE.test(shellyIdTrim);
    const shellyIdInvalid = shellyIdTrim !== '' && !shellyIdValid;
    // Vlastni prefix Shelly Gen2+ NAHRAZUJE ID zarizeni, proto musi koncit ID zarizeni.
    const topicBase = hasObjekt
        ? `shelly/${username}/o${objektId}`
        : `shelly/${username}/o<ID objektu>`;
    const topicPrefix = `${topicBase}/${shellyIdValid ? shellyIdTrim : '<ID zařízení>'}`;

    // =====================
    // RENDER STEPS
    // =====================

    const renderStep0 = () => (
        <Box>
            <Typography gutterBottom>
                Jaké zařízení chcete připojit? Podle typu vás průvodce provede správným nastavením.
            </Typography>
            <RadioGroup
                value={deviceType}
                onChange={(e) => setDeviceType(e.target.value as DeviceType)}
                sx={{ mt: 1, gap: 1 }}
            >
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControlLabel
                        value="SHELLY"
                        control={<Radio />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WifiIcon color="primary" />
                                <Box>
                                    <Typography fontWeight={600}>Shelly / WiFi</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Bezdrátové zařízení Shelly připojené přímo přes WiFi
                                        (např. Shelly Plus Smoke). Nastavuje se v mobilní aplikaci Shelly.
                                    </Typography>
                                </Box>
                            </Box>
                        }
                    />
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControlLabel
                        value="RPI"
                        control={<Radio />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RouterIcon color="primary" />
                                <Box>
                                    <Typography fontWeight={600}>Raspberry Pi / Zigbee</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Brána Raspberry Pi se Zigbee senzory. Nahrává se konfigurační soubor.
                                    </Typography>
                                </Box>
                            </Box>
                        }
                    />
                </Paper>
            </RadioGroup>
        </Box>
    );

    const renderCredentialsCommon = () => (
        <>
            <CopyField
                label="MQTT Broker"
                value={credentials?.brokerUrl ?? ''}
                onCopy={handleCopy}
            />
            <CopyField
                label="MQTT Username"
                value={username}
                onCopy={handleCopy}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    label="MQTT Heslo"
                    value={
                        revealedPassword
                            ? showPassword
                                ? revealedPassword
                                : '••••••••••••'
                            : 'Heslo je skryto'
                    }
                    fullWidth
                    margin="normal"
                    disabled
                    size="small"
                    InputProps={{
                        endAdornment: revealedPassword ? (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword((v) => !v)} size="small">
                                    {showPassword ? (
                                        <VisibilityOffIcon fontSize="small" />
                                    ) : (
                                        <VisibilityIcon fontSize="small" />
                                    )}
                                </IconButton>
                                <IconButton onClick={() => handleCopy(revealedPassword, 'Heslo')} size="small">
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
                <Button
                    onClick={handleShowPassword}
                    variant="outlined"
                    startIcon={
                        showPasswordMutation.isPending ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <VisibilityIcon />
                        )
                    }
                    disabled={showPasswordMutation.isPending}
                    sx={{ mt: 2, whiteSpace: 'nowrap', minWidth: 170 }}
                >
                    Zobrazit heslo
                </Button>
            </Box>
        </>
    );

    const renderStep1Shelly = () => {
        const { host, port } = broker;
        return (
            <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Tyto údaje zadáte v mobilní aplikaci Shelly. Heslo zobrazíte tlačítkem
                    „Zobrazit heslo" — je stejné pro všechna zařízení této jednotky.
                </Alert>
                {renderCredentialsCommon()}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <CopyField label="Server (host)" value={host} onCopy={handleCopy} />
                    <CopyField label="Port" value={port} onCopy={handleCopy} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                    V Shelly zapněte SSL/TLS a zadejte server a port zvlášť.
                </Typography>

                <TextField
                    label="ID zařízení (volitelné)"
                    placeholder="shellyplugsg3-aa0001"
                    value={shellyId}
                    onChange={(e) => setShellyId(e.target.value)}
                    error={shellyIdInvalid}
                    helperText={
                        shellyIdInvalid
                            ? 'Povolena jsou písmena, číslice, pomlčka a podtržítko (max. 128 znaků).'
                            : 'Vyplňte, aby zkopírovaný prefix byl kompletní.'
                    }
                    size="small"
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ 'aria-label': 'ID zařízení' }}
                />

                <CopyField
                    label="MQTT topic prefix"
                    value={topicPrefix}
                    onCopy={handleCopy}
                    helperText={
                        (hasObjekt
                            ? ''
                            : 'ID objektu doplňte podle detailu konkrétního objektu (spusťte průvodce z jeho detailu). ') +
                        'Na konec doplňte ID zařízení (najdete ho na štítku nebo v aplikaci Shelly, např. shellyplugsg3-aa0001). Vlastní prefix nahradí ID zařízení, proto ho musíte zadat ručně.'
                    }
                />
            </Box>
        );
    };

    const renderStep1Rpi = () => (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Pro Raspberry Pi stáhněte konfigurační soubor — MQTT heslo je v něm již vloženo.
            </Alert>
            {renderCredentialsCommon()}

            <Divider sx={{ my: 2 }} />

            {!hasObjekt ? (
                <Alert severity="warning">
                    Konfigurační soubor lze stáhnout až z detailu konkrétního objektu.
                    Nejprve objekt vytvořte a průvodce spusťte znovu z jeho detailu.
                </Alert>
            ) : (
                <>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, mr: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Automatická instalace (doporučeno)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Soubor pro bootstrap skript na Raspberry Pi — automaticky nastaví
                                    Zigbee2MQTT i připojení k serveru.
                                </Typography>
                            </Box>
                            <Button
                                onClick={handleDownloadBootstrap}
                                variant="contained"
                                size="small"
                                startIcon={
                                    downloadBootstrapMutation.isPending ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <DownloadIcon />
                                    )
                                }
                                disabled={downloadBootstrapMutation.isPending}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                jsdh-config.json
                            </Button>
                        </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, mr: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Manuální konfigurace
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Soubor pro Zigbee2MQTT. Pro ruční instalaci zkopírujte do
                                    /opt/zigbee2mqtt/data/.
                                </Typography>
                            </Box>
                            <Button
                                onClick={handleDownloadConfig}
                                variant="outlined"
                                size="small"
                                startIcon={
                                    downloadConfigMutation.isPending ? (
                                        <CircularProgress size={16} />
                                    ) : (
                                        <DownloadIcon />
                                    )
                                }
                                disabled={downloadConfigMutation.isPending}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                configuration.yaml
                            </Button>
                        </Box>
                    </Paper>
                </>
            )}
        </Box>
    );

    const renderStep1 = () => {
        if (credentialsLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }
        return deviceType === 'SHELLY' ? renderStep1Shelly() : renderStep1Rpi();
    };

    const renderStep2Shelly = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Nastavení v aplikaci Shelly
            </Typography>
            <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
                <li>Otevřete mobilní aplikaci Shelly a vyberte své zařízení.</li>
                <li>V nastavení zařízení najděte sekci <strong>MQTT</strong> a zapněte ji.</li>
                <li>
                    Zadejte <strong>server a port</strong> z kroku „MQTT přístup" a zapněte
                    <strong> SSL/TLS</strong>.
                </li>
                <li>
                    Vyplňte <strong>uživatelské jméno</strong> a <strong>heslo</strong> z kroku
                    „MQTT přístup".
                </li>
                <li>
                    Do pole pro vlastní MQTT prefix zadejte <strong>topic prefix</strong>
                    {hasObjekt ? ` (${topicPrefix})` : ''} z kroku „MQTT přístup". Vlastní prefix
                    nahradí ID zařízení, proto musí na konci obsahovat ID tohoto zařízení
                    (najdete ho na štítku nebo v aplikaci Shelly).
                </li>
                <li>Nastavení uložte a zařízení případně restartujte.</li>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
                Přesné názvy položek se mohou lišit podle verze aplikace Shelly, princip (server, port,
                SSL, jméno, heslo, prefix) je ale vždy stejný.
            </Alert>
        </Box>
    );

    const renderStep2Rpi = () => (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Nastavení Raspberry Pi
            </Typography>
            <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
                <li>
                    Stáhněte konfigurační soubor z kroku „MQTT přístup"
                    (<strong>jsdh-config.json</strong> pro automatickou instalaci nebo
                    <strong> configuration.yaml</strong> pro ruční).
                </li>
                <li>
                    U automatické instalace nahrajte <strong>jsdh-config.json</strong> na Raspberry Pi
                    a spusťte bootstrap skript — sám nastaví Zigbee2MQTT i připojení.
                </li>
                <li>
                    Pro ruční instalaci zkopírujte <strong>configuration.yaml</strong> do
                    <strong> /opt/zigbee2mqtt/data/</strong> a restartujte Zigbee2MQTT.
                </li>
                <li>Spárujte Zigbee senzory dle návodu k Raspberry Pi.</li>
            </Box>
        </Box>
    );

    const renderStep2 = () => (deviceType === 'SHELLY' ? renderStep2Shelly() : renderStep2Rpi());

    const renderTimeoutChecklist = (always: boolean) =>
        (always || showTimeoutHint) && (
            <Alert severity={always ? 'info' : 'warning'} sx={{ mt: 3, textAlign: 'left' }}>
                <AlertTitle>{always ? 'Při potížích zkontrolujte' : 'Zatím žádná data'}</AlertTitle>
                Zkontrolujte prosím:
                <Box component="ul" sx={{ pl: 3, mt: 1, mb: 0 }}>
                    <li>server, port a zapnuté SSL/TLS,</li>
                    <li>uživatelské jméno a heslo,</li>
                    <li>topic prefix{hasObjekt ? ` (${topicPrefix})` : ''} včetně ID zařízení na konci,</li>
                    <li>že je zařízení zapnuté a v dosahu WiFi.</li>
                </Box>
                Průvodce lze kdykoli zavřít — ověřit připojení můžete i později v detailu objektu.
            </Alert>
        );

    const renderStep3 = () => {
        if (!hasObjekt) {
            return (
                <Alert severity="info">
                    <AlertTitle>Ověření proběhne v detailu objektu</AlertTitle>
                    Automatické ověření připojení je vázané na konkrétní objekt. Nejprve objekt
                    vytvořte (tlačítko „Přidat objekt") a průvodce spusťte znovu z jeho detailu — pak
                    zde uvidíte, jakmile dorazí první data ze zařízení.
                </Alert>
            );
        }

        if (sensorDetected) {
            return (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                        Zařízení připojeno, senzor detekován
                    </Typography>
                    <Typography color="text.secondary">
                        {sensorCount === 1
                            ? 'Objevil se 1 senzor.'
                            : `Objevilo se ${sensorCount} senzorů.`}{' '}
                        Zprovoznění je hotové — průvodce můžete dokončit.
                    </Typography>
                </Box>
            );
        }

        if (deviceType === 'SHELLY') {
            return (
                <Box sx={{ py: 1 }}>
                    <Alert severity="info">
                        <AlertTitle>Jak připojení ověříte</AlertTitle>
                        Detektory (např. kouřové čidlo) se v seznamu objeví až po prvním poplachu nebo
                        testu. Připojení spínače je vidět v Evidenci a na kartě zařízení jako stav
                        spojení. Průvodce můžete dokončit — žádné čekání na čidlo není potřeba.
                    </Alert>
                    {renderTimeoutChecklist(true)}
                </Box>
            );
        }

        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                    Čekání na první data ze zařízení…
                </Typography>
                <Typography color="text.secondary">
                    Jakmile zařízení odešle první zprávu, senzor se objeví automaticky. Toto okno
                    můžete nechat otevřené.
                </Typography>
                {renderTimeoutChecklist(false)}
            </Box>
        );
    };

    const renderStepContent = () => {
        switch (krok) {
            case 0:
                return renderStep0();
            case 1:
                return renderStep1();
            case 2:
                return renderStep2();
            case 3:
                return renderStep3();
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Průvodce zprovozněním{objektNazev ? ` — ${objektNazev}` : ''}
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 1, mb: 3 }}>
                    {stepLabels().map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{ minHeight: 260 }}>{renderStepContent()}</Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose} color="inherit">
                    Zavřít
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={handleBack} disabled={activeStep === 0}>
                        Zpět
                    </Button>
                    <Button onClick={handleNext} variant="contained">
                        {isLastStep ? 'Dokončit' : 'Další'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default OnboardingWizard;
